import { ARTIFACT_ORDER, getArtifact, isDetailArtifact } from '@/components/Admin/MetricStudio/core/artifactRegistry';

const ADMIN_TAB_META = { adminTab: 'Performance Domains & Metrics' };
const ARTIFACT_PATTERN = ARTIFACT_ORDER.join('|');
const DETAIL_ARTIFACT_PATTERN = ARTIFACT_ORDER.filter(isDetailArtifact).join('|');
const ARTIFACT_ROUTE_SEGMENTS = {
  wizard: 'wizard',
  metric: 'metrics',
  flag: 'patient-flags',
  worklist: 'worklists',
  insight: 'metric-insights',
  attribute: 'data-attributes',
  quality: 'quality-metrics',
  engagement: 'entity-engagement',
  structured: 'structured-analytics',
};
const ARTIFACT_ROUTE_PATTERN = Object.values(ARTIFACT_ROUTE_SEGMENTS).join('|');
const DETAIL_ARTIFACT_ROUTE_PATTERN = ARTIFACT_ORDER
  .filter(isDetailArtifact)
  .map((key) => ARTIFACT_ROUTE_SEGMENTS[key])
  .join('|');
// Tenant Admin renders its panel from route meta rather than a nested
// <RouterView>, but Vue Router still requires a route component.
const RouteState = { render: () => null };

export function artifactKeyFor(value) {
  if (getArtifact(value)) return value;
  return Object.entries(ARTIFACT_ROUTE_SEGMENTS)
    .find(([, segment]) => segment === value)?.[0] || 'metric';
}

export function artifactRouteSegment(value) {
  return ARTIFACT_ROUTE_SEGMENTS[artifactKeyFor(value)];
}

function idFor(value) {
  return String(value || '').match(/^\d+/)?.[0] || null;
}

/**
 * Translate the retired query-string state into the canonical Studio route.
 * The old UI defaulted an omitted type to metrics, so the redirect preserves
 * that behavior for existing shared links.
 */
export function metricStudioLocation({ artifact, id } = {}) {
  const key = artifactKeyFor(artifact);
  const recordId = idFor(id);
  if (recordId && isDetailArtifact(key)) {
    return {
      name: 'AdminMetricStudioDetail',
      params: { artifact: artifactRouteSegment(key), id: recordId },
      query: {},
    };
  }
  return {
    name: 'AdminMetricStudioList',
    params: { artifact: artifactRouteSegment(key) },
    query: {},
  };
}

function legacyQueryRedirect(to) {
  return metricStudioLocation({
    artifact: to.query.type,
    id: to.query.id,
  });
}

export const METRIC_STUDIO_ROUTES = [
  // The former Tenant Admin tab URL and query-string detail links stay valid,
  // but immediately normalize to the canonical Metric Studio location.
  {
    path: 'performance-domains-metrics',
    name: 'AdminPerformanceDomainsMetrics',
    redirect: legacyQueryRedirect,
    meta: ADMIN_TAB_META,
  },
  {
    path: 'metric-studio',
    name: 'AdminMetricStudio',
    redirect: legacyQueryRedirect,
    meta: ADMIN_TAB_META,
  },
  {
    path: `metric-studio/:artifact(${DETAIL_ARTIFACT_ROUTE_PATTERN})/:id(\\d+)`,
    name: 'AdminMetricStudioDetail',
    component: RouteState,
    meta: ADMIN_TAB_META,
  },
  {
    path: `metric-studio/:artifact(${ARTIFACT_ROUTE_PATTERN})`,
    name: 'AdminMetricStudioList',
    component: RouteState,
    meta: ADMIN_TAB_META,
  },
  {
    path: `metric-studio/:artifact(${DETAIL_ARTIFACT_PATTERN})/:id(\\d+)`,
    redirect: (to) => metricStudioLocation(to.params),
    meta: ADMIN_TAB_META,
  },
  {
    path: `metric-studio/:artifact(${ARTIFACT_PATTERN})`,
    redirect: (to) => metricStudioLocation(to.params),
    meta: ADMIN_TAB_META,
  },
  // Keep the original metric-only path working without letting its numeric
  // segment collide with the canonical artifact-list route above.
  {
    path: 'metric-studio/:metricId(\\d+)',
    name: 'AdminMetricDetail',
    redirect: (to) => metricStudioLocation({ artifact: 'metric', id: to.params.metricId }),
    meta: ADMIN_TAB_META,
  },
];
